import React, { useState, useEffect, useCallback } from 'react';
import apiFetch from '../utils/api';

const sortConversations = (conversations) => {
    if (!conversations || conversations.length <= 1) return conversations;
    
    // Use a more stable sort that prioritizes pins, then timestamp
    return [...conversations].sort((a, b) => {
        // Pin priority
        if (a.isPinned !== b.isPinned) {
            return a.isPinned ? -1 : 1;
        }
        
        // Timestamp priority (newest first)
        const timeA = a.rawTimestamp ? new Date(a.rawTimestamp).getTime() : 0;
        const timeB = b.rawTimestamp ? new Date(b.rawTimestamp).getTime() : 0;
        
        if (timeB !== timeA) {
            return timeB - timeA;
        }
        
        // Final tie-breaker (id)
        return String(b.id).localeCompare(String(a.id));
    });
};

/**
 * Custom hook for managing conversations
 * @param {Object} socket - Socket.IO instance
 * @returns {Object} Conversation state and handlers
 */
// Maximum number of conversations to keep messages in memory
const MAX_CACHED_CONVERSATIONS = 5;

export const useConversations = (socket) => {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const selectedId = selectedConversation && selectedConversation.contact ? String(selectedConversation.contact.phone).replace(/\D/g, '') : null;
    const [messagesByConversation, setMessagesByConversation] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [aiStatesByPhone, setAiStatesByPhone] = useState({});
    const [globalDefaultAi, setGlobalDefaultAi] = useState(true);

    // Conversation list pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Message pagination state (per conversation)
    const [messagePaginationByPhone, setMessagePaginationByPhone] = useState({});
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const isLoadingOlderRef = React.useRef(false); // ref for use inside callbacks without stale closure
    // Track order of conversation access for cache eviction (LRU)
    const conversationAccessOrder = React.useRef([]);

    // Fetch global settings
    useEffect(() => {
        const fetchGlobalSettings = async () => {
            try {
                const response = await apiFetch('/api/settings');
                if (response.ok) {
                    const data = await response.json();
                    if (data.settings && data.settings.default_ai_enabled !== undefined) {
                        const isEnabled = String(data.settings.default_ai_enabled) === 'true';
                        setGlobalDefaultAi(isEnabled);
                        console.log('⚙️ Global default AI setting:', isEnabled);
                    }
                }
            } catch (error) {
                console.error('❌ Error fetching global settings:', error);
            }
        };
        fetchGlobalSettings();
    }, []);

    // Fetch all conversations with pagination
    const fetchConversations = useCallback(async (page = 1, search = '', append = false, tagId = null, startDate = null, endDate = null, unreadOnly = false, silent = false, leadTime = null, channel = null) => {
        try {
            if (!append && !silent) setIsLoading(true);
            else if (append) setIsLoadingMore(true);

            console.log(`🔄 Fetching conversations (page ${page}, search: "${search}", tag: ${tagId}, date: ${startDate} to ${endDate}, unread: ${unreadOnly})...`);

            const params = new URLSearchParams({
                page: page.toString(),
                limit: '100', // Changed from 50 to 100
                _t: Date.now().toString() // Cache busting
            });

            if (search) {
                params.append('search', search);
            }

            if (tagId) {
                params.append('tagId', tagId);
            }

            if (startDate) {
                params.append('startDate', startDate);
            }

            if (endDate) {
                params.append('endDate', endDate);
            }

            if (unreadOnly) {
                params.append('unreadOnly', 'true');
            }

            if (leadTime) {
                params.append('leadTime', leadTime);
            }

            if (channel && channel !== 'all') {
                params.append('channel', channel);
            }

            const response = await apiFetch(`/api/conversations?${params}`, {
                cache: 'no-cache'
            });
            if (!response.ok) throw new Error('Error fetching conversations');

            const data = await response.json();

            // Handle both paginated and legacy response formats
            const newConversations = Array.isArray(data) ? data : (data.data || []);

            // Check if there are more pages
            if (data.pagination) {
                setHasMore(data.pagination.hasNext);
                setCurrentPage(data.pagination.page);
            } else {
                setHasMore(false);
            }

            if (append) {
                // Append and deduplicate by phone
                setConversations(prev => {
                    const existingPhones = new Set(prev.map(c => c && c.contact ? c.contact.phone : null).filter(Boolean));
                    const uniqueNew = newConversations.filter(c => c && c.contact && !existingPhones.has(c.contact.phone));
                    return [...prev, ...uniqueNew];
                });
            } else {
                setConversations(newConversations);
            }

            // Initialize AI states from loaded conversations
            const initialAiStates = {};
            newConversations.forEach(conv => {
                if (conv && conv.contact) {
                    initialAiStates[conv.contact.phone] = conv.aiEnabled;
                }
            });
            setAiStatesByPhone(prev => ({ ...prev, ...initialAiStates }));

            console.log(`✅ Loaded ${newConversations.length} conversations (page ${page})`);

            return data;
        } catch (error) {
            console.error('❌ Error fetching conversations:', error);
            return [];
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, []);

    // Load more conversations (for infinite scroll)
    const loadMoreConversations = useCallback(async (tagId = null, startDate = null, endDate = null, unreadOnly = false, leadTime = null, channel = null) => {
        if (!hasMore || isLoadingMore) return;
        await fetchConversations(currentPage + 1, searchQuery, true, tagId, startDate, endDate, unreadOnly, false, leadTime, channel);
    }, [currentPage, hasMore, isLoadingMore, searchQuery, fetchConversations]);

    // Search conversations (server-side)
    const searchConversations = useCallback(async (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
        setHasMore(true);
        await fetchConversations(1, query, false);
    }, [fetchConversations]);

    // Fetch messages for a specific conversation (initial load, most recent)
    const fetchMessages = useCallback(async (phone) => {
        try {
            setIsLoadingMessages(true);
            console.log(`🔄 Fetching messages for ${phone}...`);

            const response = await apiFetch(`/api/conversations/${phone}/messages?limit=50`);
            if (!response.ok) throw new Error('Error fetching messages');

            const data = await response.json();
            // Handle both paginated and legacy response formats
            const messages = Array.isArray(data) ? data : (data.data || []);
            const pagination = Array.isArray(data) ? null : data.pagination;

            // Log first message for debugging
            if (messages.length > 0) {
                console.log('📅 First message rawTimestamp:', messages[0].rawTimestamp);
            }

            // Store pagination info for this conversation
            if (pagination) {
                setMessagePaginationByPhone(prev => ({
                    ...prev,
                    [phone]: {
                        hasMore: pagination.hasMore,
                        // oldest message timestamp = cursor for loading even older messages
                        oldestCursor: messages.length > 0 ? messages[0].rawTimestamp : null
                    }
                }));
            }

            // Update LRU access order
            conversationAccessOrder.current = [
                phone,
                ...conversationAccessOrder.current.filter(p => p !== phone)
            ].slice(0, MAX_CACHED_CONVERSATIONS);

            // Evict oldest conversation from message cache if over limit
            setMessagesByConversation(prev => {
                const next = { ...prev, [phone]: messages };
                const accessOrder = conversationAccessOrder.current;
                // Remove conversations not in the recent access order
                Object.keys(next).forEach(p => {
                    if (!accessOrder.includes(p)) {
                        delete next[p];
                    }
                });
                return next;
            });

            console.log(`✅ Loaded ${messages.length} messages for ${phone} (hasMore: ${pagination?.hasMore})`);
            return data;
        } catch (error) {
            console.error(`❌ Error fetching messages for ${phone}:`, error);
            return [];
        } finally {
            setIsLoadingMessages(false);
        }
    }, []);

    // Load older messages (scroll-to-top infinite scroll)
    const loadOlderMessages = useCallback(async (phone) => {
        const paginationInfo = messagePaginationByPhone[phone];
        if (!paginationInfo?.hasMore || isLoadingOlderRef.current) return false;
        if (!paginationInfo?.oldestCursor) return false;

        try {
            isLoadingOlderRef.current = true;
            setIsLoadingOlderMessages(true);
            console.log(`🔄 Loading older messages for ${phone} before ${paginationInfo.oldestCursor}...`);

            const response = await apiFetch(
                `/api/conversations/${phone}/messages?limit=30&before=${encodeURIComponent(paginationInfo.oldestCursor)}`
            );
            if (!response.ok) throw new Error('Error fetching older messages');

            const data = await response.json();
            const olderMessages = Array.isArray(data) ? data : (data.data || []);
            const pagination = Array.isArray(data) ? null : data.pagination;

            if (olderMessages.length === 0) {
                setMessagePaginationByPhone(prev => ({
                    ...prev,
                    [phone]: { ...prev[phone], hasMore: false }
                }));
                return false;
            }

            // Prepend older messages to existing ones
            setMessagesByConversation(prev => {
                const current = prev[phone] || [];
                // Deduplicate by id
                const existingIds = new Set(current.map(m => m.id));
                const uniqueOlder = olderMessages.filter(m => !existingIds.has(m.id));
                return { ...prev, [phone]: [...uniqueOlder, ...current] };
            });

            // Update cursor to the oldest of the new batch
            if (pagination) {
                const newOldest = olderMessages.length > 0 ? olderMessages[0].rawTimestamp : null;
                setMessagePaginationByPhone(prev => ({
                    ...prev,
                    [phone]: {
                        hasMore: pagination.hasMore,
                        oldestCursor: newOldest || prev[phone]?.oldestCursor
                    }
                }));
            }

            console.log(`✅ Loaded ${olderMessages.length} older messages for ${phone}`);
            return true;
        } catch (error) {
            console.error(`❌ Error loading older messages for ${phone}:`, error);
            return false;
        } finally {
            isLoadingOlderRef.current = false;
            setIsLoadingOlderMessages(false);
        }
    }, [messagePaginationByPhone]);

    // Mark conversation as read
    const markConversationAsRead = useCallback(async (phone) => {
        try {
            await apiFetch(`/api/conversations/${phone}/mark-read`, { method: 'POST' });

            setConversations(prev => prev.map(conv =>
                (conv && conv.contact && conv.contact.phone === phone) ? { ...conv, unread: 0 } : conv
            ));
        } catch (error) {
            console.error('❌ Error marking as read:', error);
        }
    }, []);

    // Mark conversation as unread
    const markConversationAsUnread = useCallback(async (phone) => {
        try {
            await apiFetch(`/api/conversations/${phone}/mark-unread`, { method: 'POST' });

            setConversations(prev => prev.map(conv =>
                (conv && conv.contact && conv.contact.phone === phone) ? { ...conv, unread: 1 } : conv
            ));
        } catch (error) {
            console.error('❌ Error marking as unread:', error);
        }
    }, []);

    // Select a conversation
    const selectConversation = useCallback(async (conversation) => {
        // Handle deselection
        if (!conversation) {
            if (selectedConversation && selectedConversation.contact && socket) {
                socket.emit('leave-conversation', selectedConversation.contact.phone);
            }
            setSelectedConversation(null);
            return;
        }

        if (!conversation.contact) return; // Guard against malformed conversation selection
        const phone = conversation.contact.phone;
        console.log('🎯 Selecting conversation:', phone);

        // Ensure AI state is tracked for this conversation
        if (aiStatesByPhone[phone] === undefined && conversation.aiEnabled !== undefined) {
            setAiStatesByPhone(prev => ({ ...prev, [phone]: conversation.aiEnabled }));
        }

        // Leave previous room if any
        if (selectedConversation && selectedConversation.contact && socket) {
            socket.emit('leave-conversation', selectedConversation.contact.phone);
        }

        setSelectedConversation(conversation);

        // Join new room for real-time updates
        if (socket) {
            console.log('📱 Joining socket room for:', phone);
            socket.emit('join-conversation', phone);
        }

        if (conversation.unread > 0) {
            await markConversationAsRead(phone);
        }

        await fetchMessages(phone);
    }, [fetchMessages, selectedConversation, socket, markConversationAsRead]);

    // Send a message
    const sendMessage = useCallback(async (phone, message, name, options = {}) => {
        const tempId = Date.now();
        const currentAIState = Boolean(aiStatesByPhone[phone] ?? globalDefaultAi);
        const { agentId, agentName, replyTo } = options;

        const newMessage = {
            id: tempId,
            text: message,
            sender: 'agent', // Always 'agent' so it appears on the right
            agent_name: agentName, // Display name
            timestamp: new Date().toLocaleTimeString('es-CO', {
                hour: '2-digit',
                minute: '2-digit'
            }),
            rawTimestamp: new Date().toISOString(),
            status: 'sending',
            replyTo: options.replyToFull || (replyTo ? { id: replyTo } : null)
        };

        // Optimistic update
        setMessagesByConversation(prev => ({
            ...prev,
            [phone]: [...(prev[phone] || []), newMessage]
        }));

        setConversations(prev => {
            const currentConversations = [...prev];
            const cleanIncomingPhone = String(phone).replace(/\D/g, '');
            const conversationIndex = currentConversations.findIndex(conv =>
                conv && conv.contact && String(conv.contact.phone).replace(/\D/g, '') === cleanIncomingPhone
            );

            if (conversationIndex !== -1) {
                const targetConv = currentConversations[conversationIndex];
                if (!targetConv || !targetConv.contact) return currentConversations;

                const updatedConv = {
                    ...targetConv,
                    lastMessage: message,
                    timestamp: newMessage.timestamp,
                    rawTimestamp: newMessage.rawTimestamp,
                    // When WE respond, clear SLA tracking immediately (don't wait for job)
                    leadTime: null,
                    lastMessageFromMe: true
                };

                currentConversations.splice(conversationIndex, 1);
                return sortConversations([updatedConv, ...currentConversations]);
            }
            return currentConversations;
        });

        try {
            const response = await apiFetch('/api/send-message', {
                method: 'POST',
                body: JSON.stringify({
                    phone: String(phone),
                    message,
                    name,
                    temp_id: tempId,
                    ai_enabled: currentAIState,
                    agent_id: agentId,
                    agent_name: agentName,
                    reply_to: replyTo
                })
            });

            if (!response.ok) throw new Error('Error sending message');

            const data = await response.json();

            setMessagesByConversation(prev => ({
                ...prev,
                [phone]: prev[phone].map(msg =>
                    (msg.id === tempId || String(msg.id) === String(tempId)) ? {
                        ...msg,
                        status: data.newMessage?.status || 'sent',
                        id: data.newMessage?.whatsapp_id || data.newMessage?.id || msg.id,
                        whatsapp_id: data.newMessage?.whatsapp_id || msg.whatsapp_id,
                        _tempId: String(tempId), // keep original tempId for socket dedup
                        replyTo: data.newMessage?.replyTo || msg.replyTo
                    } : msg
                )
            }));

            return true;
        } catch (error) {
            console.error('❌ Error sending message:', error);
            setMessagesByConversation(prev => ({
                ...prev,
                [phone]: prev[phone].map(msg =>
                    msg.id === tempId ? { ...msg, status: 'failed' } : msg
                )
            }));
            return false;
        }
    }, [aiStatesByPhone, selectedId, globalDefaultAi]);

    const sendFile = useCallback(async (phone, file, caption, name, options = {}) => {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const { agentId, agentName, replyTo, replyToFull } = options;
        const media_type = file.type.startsWith('image/') ? 'image' :
            file.type.startsWith('video/') ? 'video' :
                file.type.startsWith('audio/') ? 'audio' : 'document';

        // ── OPTIMISTIC UPDATE ──────────────────────────────────────────────────
        // Create a local blob URL so the image shows instantly without waiting
        const localBlobUrl = URL.createObjectURL(file);
        const optimisticMsg = {
            id: tempId,
            whatsapp_id: tempId,
            sender: 'agent',
            sender_name: agentName || 'Tú',
            agent_name: agentName || 'Tú',
            text: caption || '',
            media_url: localBlobUrl,
            media_type,
            timestamp: new Date().toLocaleTimeString('es-CO', {
                hour: '2-digit',
                minute: '2-digit'
            }),
            rawTimestamp: new Date().toISOString(),  // needed for socket duplicate detection
            reactions: [],        // explicitly empty array to prevent map errors
            _isOptimistic: true,  // flag to identify and replace later
        };

        // Add optimistic message to the conversation immediately
        setMessagesByConversation(prev => {
            const existing = prev[phone] || [];
            return { ...prev, [phone]: [...existing, optimisticMsg] };
        });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('phone', phone);
        formData.append('name', name);
        formData.append('temp_id', tempId);
        if (caption) formData.append('caption', caption);
        if (agentId) formData.append('agent_id', agentId);
        if (agentName) formData.append('agent_name', agentName);
        if (replyTo) formData.append('reply_to', replyTo);

        try {
            const response = await apiFetch('/api/send-file', {
                method: 'POST',
                // apiFetch handles standard headers, but for FormData we should let the browser set it
                headers: {},
                body: formData
            });

            if (!response.ok) throw new Error('Error sending file');

            const result = await response.json();

            // ✅ SUCCESS: update the optimistic message in-place with the real data from the server.
            // Do NOT remove it and wait for the socket — the socket may be slow, filtered as duplicate,
            // or missed entirely, causing the message to disappear until a page refresh.
            const realMessage = result.newMessage || result.message || result;
            const realMediaUrl = realMessage?.media_url || realMessage?.mediaUrl || localBlobUrl;
            const realId = realMessage?.whatsapp_id || realMessage?.id || tempId;

            setMessagesByConversation(prev => {
                const msgs = prev[phone] || [];
                return {
                    ...prev,
                    [phone]: msgs.map(m =>
                        (m.id === tempId || String(m.id) === String(tempId))
                            ? {
                                ...m,
                                id: realId,
                                whatsapp_id: realId,
                                media_url: realMediaUrl,
                                status: realMessage?.status || 'sent',
                                _isOptimistic: false,
                                replyTo: realMessage?.replyTo || m.replyTo
                            }
                            : m
                    )
                };
            });

            // Revoke the local blob URL if we got a real URL from the server
            if (realMediaUrl !== localBlobUrl) {
                URL.revokeObjectURL(localBlobUrl);
            }

            // Update conversation list order
            setConversations(prev => {
                const currentConversations = [...prev];
                const cleanIncomingPhone = String(phone).replace(/\D/g, '');
                const conversationIndex = currentConversations.findIndex(conv =>
                    conv && conv.contact && String(conv.contact.phone).replace(/\D/g, '') === cleanIncomingPhone
                );

                if (conversationIndex !== -1) {
                    const targetConv = currentConversations[conversationIndex];
                    const updatedConv = {
                        ...targetConv,
                        lastMessage: caption || (
                            media_type === 'image' ? '📷 Imagen' :
                                media_type === 'video' ? '🎥 Video' :
                                    media_type === 'audio' ? '🎵 Audio' : `📎 ${file.name}`
                        ),
                        timestamp: new Date().toLocaleTimeString('es-CO', {
                            hour: '2-digit',
                            minute: '2-digit'
                        }),
                        rawTimestamp: new Date().toISOString(),
                        // Clear SLA tracking when agent sends a file
                        leadTime: null,
                        lastMessageFromMe: true
                    };
                    currentConversations.splice(conversationIndex, 1);
                    return sortConversations([updatedConv, ...currentConversations]);
                }
                return currentConversations;
            });

            return result;
        } catch (error) {
            console.error('❌ Error sending file:', error);

            // ❌ FAILURE: mark the optimistic message as failed
            setMessagesByConversation(prev => {
                const msgs = prev[phone] || [];
                return {
                    ...prev,
                    [phone]: msgs.map(m =>
                        (m.id === tempId || String(m.id) === String(tempId))
                            ? { ...m, status: 'failed', _errorText: 'No se pudo enviar' }
                            : m
                    )
                };
            });

            // NOTE: we keep the blob URL alive so the user can see the failed image
            // It will be cleaned up when the message is dismissed
            throw error;
        }
    }, []);

    // Toggle AI for a conversation
    const toggleAI = useCallback(async (phone) => {
        const currentState = Boolean(aiStatesByPhone[phone] ?? globalDefaultAi);
        const newState = !currentState;

        // Optimistic update
        setAiStatesByPhone(prev => ({ ...prev, [phone]: newState }));

        try {
            const response = await apiFetch(`/api/conversations/${phone}/toggle-ai`, {
                method: 'POST',
                body: JSON.stringify({ aiEnabled: newState })
            });

            if (!response.ok) {
                // Revert on error
                setAiStatesByPhone(prev => ({ ...prev, [phone]: currentState }));
                throw new Error('Error toggling AI');
            }

            console.log(`✅ AI ${newState ? 'enabled' : 'disabled'} for ${phone}`);
        } catch (error) {
            console.error('❌ Error toggling AI:', error);
            setAiStatesByPhone(prev => ({ ...prev, [phone]: currentState }));
        }
    }, [aiStatesByPhone, globalDefaultAi]);

    // Toggle Pin for a conversation
    const togglePin = useCallback(async (phone) => {
        const conv = conversations.find(c => c && c.contact && c.contact.phone === phone);
        if (!conv) return;

        const currentPinnedState = Boolean(conv.isPinned);
        const newState = !currentPinnedState;

        // Optimistic update
        setConversations(prev => {
            const updated = prev.map(c =>
                c && c.contact && c.contact.phone === phone ? { ...c, isPinned: newState } : c
            );
            // Re-sort to reflect new pin state
            return sortConversations(updated);
        });

        try {
            const response = await apiFetch(`/api/conversations/${phone}/pin`, {
                method: 'PUT',
                body: JSON.stringify({ isPinned: newState })
            });

            if (!response.ok) {
                throw new Error('Error toggling pin status');
            }
        } catch (error) {
            console.error('❌ Error toggling pin:', error);
            // Revert on error
            setConversations(prev => {
                const updated = prev.map(c =>
                    c && c.contact && c.contact.phone === phone ? { ...c, isPinned: currentPinnedState } : c
                );
                return sortConversations(updated);
            });
        }
    }, [conversations]);

    // Re-join conversation room on socket reconnect
    useEffect(() => {
        if (!socket || !selectedConversation) return;

        const handleReconnect = () => {
            if (selectedConversation && selectedConversation.contact) {
                const phone = selectedConversation.contact.phone;
                console.log('🔄 Socket reconnected, re-joining conversation room:', phone);
                socket.emit('join-conversation', phone);
            }
        };

        socket.on('reconnect', handleReconnect);
        // Also re-join on connect (in case this is the initial connect after the conversation was selected)
        socket.on('connect', handleReconnect);

        return () => {
            socket.off('reconnect', handleReconnect);
            socket.off('connect', handleReconnect);
        };
    }, [socket, selectedConversation]);

    // Handle incoming messages from socket
    useEffect(() => {
        if (!socket) return;

        const handleSocketMessage = (messageData, isAgentEvent = false) => {
            // Determine if agent/outgoing message based on event type OR payload
            const isAgent = isAgentEvent ||
                messageData.sender_type === 'agent' ||
                messageData.sender === 'agent' ||
                messageData.sender_type === 'ai' ||
                messageData.sender === 'ai';

            console.log(`📨 ${isAgent ? 'Agent' : 'Customer'} message received:`, messageData);

            const phone = messageData.phone;
            const senderType = messageData.sender_type || messageData.sender || (isAgent ? 'agent' : 'customer');
            const cleanIncomingPhone = String(phone).replace(/\D/g, '');

            // Ignore messages sent by agent from THIS session - they were already added optimistically
            // (We check whatsapp_id if available to avoid duplicates)

            const formattedMessage = {
                id: messageData.whatsapp_id || messageData.id || Date.now(),
                text: messageData.message || messageData.text,
                sender: senderType,
                timestamp: new Date(messageData.timestamp).toLocaleTimeString('es-CO', {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                rawTimestamp: messageData.timestamp || new Date().toISOString(),
                status: 'delivered',
                // Media support
                media_type: messageData.media_type || messageData.mediaType || null,
                media_url: messageData.media_url || messageData.mediaUrl || null,
                agent_name: messageData.agent_name || null,
                sender_name: messageData.sender_name || messageData.pushName || messageData.contact_name || null,
                temp_id: messageData.temp_id, // Crucial for duplicate matching
                replyTo: messageData.replyTo || null
            };

            // Check for duplicates before adding
            setMessagesByConversation(prev => {
                const existingMessages = prev[phone] || [];

                // Duplicate check by ID or content/timestamp
                // Priority:
                // 1. Check by ID (whatsapp_id or id)
                // 2. Check by temp_id (most reliable for outgoing)
                // 3. For OUTGOING messages without temp_id, check by content (legacy fallback)
                const isDuplicate = existingMessages.some(msg => {
                    // 1. Check by exact ID match
                    if (msg.id === formattedMessage.id) return true;

                    // 2. Check by whatsapp_id (both directions)
                    if (msg.whatsapp_id && formattedMessage.whatsapp_id &&
                        msg.whatsapp_id === formattedMessage.whatsapp_id) return true;

                    // 3. Check: socket whatsapp_id matches stored id (after optimistic update)
                    if (formattedMessage.whatsapp_id && msg.id &&
                        String(msg.id) === String(formattedMessage.whatsapp_id)) return true;

                    // 4. Check by _tempId (stored on optimistic msg after API response)
                    if (msg._tempId && formattedMessage.temp_id &&
                        String(msg._tempId) === String(formattedMessage.temp_id)) return true;

                    // 5. Check by temp_id for outgoing optimistic messages
                    if (formattedMessage.temp_id && String(msg.id) === String(formattedMessage.temp_id)) return true;
                    if (msg.temp_id && String(formattedMessage.id) === String(msg.temp_id)) return true;

                    // 6. STRICTER FALLBACK: content match for agent messages in 'sending' state
                    if (isAgent && msg.status === 'sending' && msg.sender === formattedMessage.sender) {
                        if (msg.media_type && formattedMessage.media_type) {
                            return msg.media_type === formattedMessage.media_type &&
                                Math.abs(new Date(msg.rawTimestamp) - new Date(formattedMessage.rawTimestamp)) < 20000;
                        }
                        return msg.text && msg.text === formattedMessage.text;
                    }
                    
                    return false;
                });

                if (isDuplicate) {
                    // Update status if it was 'sending'
                    if (isAgent) {
                        return {
                            ...prev,
                            [phone]: existingMessages.map(msg => {
                                // Check by temp_id if available
                                if (formattedMessage.temp_id && String(msg.id) === String(formattedMessage.temp_id)) {
                                    return {
                                        ...msg,
                                        status: 'delivered',
                                        id: formattedMessage.id,
                                        media_url: formattedMessage.media_url, // Update URL (e.g. from blob: to http:)
                                        media_type: formattedMessage.media_type,
                                        agent_name: formattedMessage.agent_name,
                                        replyTo: formattedMessage.replyTo || msg.replyTo
                                    };
                                }

                                // Fallback to content matching
                                if (!formattedMessage.temp_id && msg.status === 'sending') {
                                    // Match by media type if present
                                    if (msg.media_type || formattedMessage.media_type) {
                                        if (msg.media_type === formattedMessage.media_type &&
                                            Math.abs(new Date(msg.rawTimestamp) - new Date(formattedMessage.rawTimestamp)) < 20000) {
                                            return {
                                                ...msg,
                                                status: 'delivered',
                                                id: formattedMessage.id,
                                                media_url: formattedMessage.media_url,
                                                media_type: formattedMessage.media_type,
                                                agent_name: formattedMessage.agent_name
                                            };
                                        }
                                    }
                                    // Match by text content
                                    else if (msg.text && msg.text === formattedMessage.text) {
                                        return {
                                            ...msg,
                                            status: 'delivered',
                                            id: formattedMessage.id,
                                            agent_name: formattedMessage.agent_name
                                        };
                                    }
                                }
                                return msg;
                            })
                        };
                    }
                    return prev;
                }

                return { ...prev, [phone]: [...existingMessages, formattedMessage] };
            });

            setConversations(prev => {
                const index = prev.findIndex(conv =>
                    conv && conv.contact && String(conv.contact.phone).replace(/\D/g, '') === cleanIncomingPhone
                );
 
                if (index !== -1) {
                    const targetConv = prev[index];
                    if (!targetConv || !targetConv.contact) return prev;
 
                    const updatedConv = {
                        ...targetConv,
                        lastMessage: formattedMessage.text || targetConv.lastMessage,
                        timestamp: formattedMessage.timestamp,
                        rawTimestamp: formattedMessage.rawTimestamp,
                        unread: (selectedId === cleanIncomingPhone || isAgent)
                            ? 0
                            : (targetConv.unread || 0) + 1
                    };
 
                    if (index === 0 && !isAgent && !targetConv.isPinned) {
                        const next = [...prev];
                        next[0] = updatedConv;
                        return next;
                    }

                    const next = prev.filter((_, i) => i !== index);
                    return sortConversations([updatedConv, ...next]).slice(0, 500);
                }
                
                const newConv = {
                    id: phone,
                    contact: {
                        name: messageData.pushName || messageData.contact_name || `Usuario ${phone.slice(-4)}`,
                        phone: phone
                    },
                    lastMessage: formattedMessage.text,
                    timestamp: formattedMessage.timestamp,
                    rawTimestamp: formattedMessage.rawTimestamp,
                    unread: 1,
                    status: 'active',
                    aiEnabled: globalDefaultAi
                };
                return sortConversations([newConv, ...prev]).slice(0, 500);
            });
        };

        const handleConversationUpdated = (data) => {
            console.log('📋 Conversation list update received:', data);
            const cleanPhone = String(data.phone).replace(/\D/g, '');

            if (data.contact_name) {
                setSelectedConversation(prev => {
                    if (!prev?.contact || String(prev.contact.phone).replace(/\D/g, '') !== cleanPhone) return prev;
                    return { ...prev, contact: { ...prev.contact, name: data.contact_name } };
                });
            }

            setConversations(prev => {
                const currentConversations = [...prev];
                const index = currentConversations.findIndex(conv =>
                    conv && conv.contact && String(conv.contact.phone).replace(/\D/g, '') === cleanPhone
                );

                const timestamp = new Date(data.timestamp).toLocaleTimeString('es-CO', {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                if (index === -1) {
                    // New conversation!
                    const newConv = {
                        id: data.phone,
                        contact: {
                            name: data.contact_name || `Usuario ${data.phone.slice(-4)}`,
                            phone: data.phone
                        },
                        lastMessage: data.lastMessage,
                        timestamp: timestamp,
                        rawTimestamp: data.timestamp,
                        unread: (selectedId === cleanPhone) ? 0 : 1,
                        status: 'active'
                    };
                    return sortConversations([newConv, ...currentConversations]).slice(0, 500);
                }

                // Update existing
                const targetConv = currentConversations[index];
                const updatedConv = {
                    ...targetConv,
                    contact: targetConv.contact ? {
                        ...targetConv.contact,
                        name: data.contact_name || targetConv.contact.name
                    } : targetConv.contact,
                    lastMessage: data.lastMessage || targetConv.lastMessage,
                    timestamp: timestamp,
                    rawTimestamp: data.timestamp,
                    unread: (selectedId === cleanPhone) ? 0 : (data.unread !== undefined ? data.unread : (data.unreadCount ?? targetConv.unread))
                };

                currentConversations.splice(index, 1);
                return sortConversations([updatedConv, ...currentConversations]).slice(0, 500);
            });
        };

        const handleStateChange = (data) => {
            setAiStatesByPhone(prev => ({
                ...prev,
                [data.phone]: data.state === 'ai_active'
            }));
        };

        const handleMessageUpdated = (data) => {
            console.log('🔄 Message updated:', data);
            const { id, whatsapp_id, phone, status, text, media_url, media_type, edited, temp_id } = data;

            const updates = {
                ...(status && { status }),
                ...(text !== undefined && { text }),
                ...(media_url !== undefined && { media_url }),
                ...(media_type !== undefined && { media_type }),
                ...(edited !== undefined && { edited })
            };

            const targetId = whatsapp_id || id;
            const targetTempId = temp_id || data._tempId;
            const targetPhone = phone ? String(phone).replace(/\D/g, '') : null;

            setMessagesByConversation(prev => {
                const nextState = { ...prev };

                Object.keys(prev).forEach(k => {
                    const cleanK = String(k).replace(/\D/g, '');
                    if (!targetPhone || cleanK === targetPhone) {
                        if (nextState[k]) {
                            nextState[k] = nextState[k].map(msg => {
                                const isMatch =
                                    (targetId && (msg.id === targetId || msg.whatsapp_id === targetId || String(msg.id) === String(targetId))) ||
                                    (targetTempId && (msg.id === targetTempId || msg.temp_id === targetTempId || msg._tempId === targetTempId || String(msg.id) === String(targetTempId)));

                                return isMatch ? { ...msg, ...updates } : msg;
                            });
                        }
                    }
                });

                return nextState;
            });

            // Also update conversation preview if text changed
            if (text !== undefined && targetPhone) {
                setConversations(prev => {
                    const currentConversations = [...prev];
                    const index = currentConversations.findIndex(c =>
                        c && c.contact && String(c.contact.phone).replace(/\D/g, '') === targetPhone
                    );

                    if (index !== -1) {
                        const targetConv = currentConversations[index];
                        currentConversations[index] = { ...targetConv, lastMessage: text };
                        return currentConversations;
                    }
                    return prev;
                });
            }
        };

        const handleStatusUpdate = (data) => {
            console.log('📊 Message status update:', data);
            handleMessageUpdated(data);
        };

        socket.on('new-message', (data) => handleSocketMessage(data, false));
        socket.on('agent-message', (data) => handleSocketMessage(data, true));
        socket.on('conversation-updated', handleConversationUpdated);
        socket.on('conversation-state-changed', handleStateChange);
        socket.on('message-updated', handleMessageUpdated);
        socket.on('message-status-update', handleStatusUpdate);

        return () => {
            socket.off('new-message');
            socket.off('agent-message');
            socket.off('conversation-updated');
            socket.off('conversation-state-changed');
            socket.off('message-updated');
            socket.off('message-status-update');
        };
    }, [socket, selectedId, selectedConversation, globalDefaultAi]);

    const updateConversationLocal = useCallback((phone, updates) => {
        setConversations(prev => prev.map(conv =>
            conv && conv.contact && String(conv.contact.phone).replace(/\D/g, '') === String(phone).replace(/\D/g, '')
                ? { ...conv, ...updates, contact: { ...conv.contact, ...(updates.contact || {}) } }
                : conv
        ));
    }, []);

    // Initial fetch removed - let parent component control it
    /*
    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);
    */

    const deleteMessage = useCallback(async (messageId, phone) => {
        try {
            // Optimistic update - Messages list
            setMessagesByConversation(prev => {
                const current = prev[phone] || [];
                // Instead of removing, we should ideally mark it as deleted if we want that effect, 
                // but if the user wants it gone, filtering is fine. 
                // However, user said "deberia parecer mensaje eliminado", implying they might want to see the placeholder 
                // OR they just want the UI to update. 
                // If I filter it out, it disappears. 
                // Let's stick to filtering out for now (or replacing with a "deleted" placeholder if preferred, but filtering is standard for "delete for everyone" in some custom apps, though WA shows placeholder).
                // Let's try replacing it with a placeholder to match WA style if that's what's requested, 
                // OR just ensure the Sidebar updates.
                // User said: "deberia parecer mensaje eliminado en el frontend" -> "Must look like deleted message".
                // I will replace the text with "🚫 Mensaje eliminado" and keep it in the list AND update sidebar.

                return {
                    ...prev,
                    [phone]: current.map(m =>
                        m.id === messageId
                            ? { ...m, text: '🚫 Mensaje eliminado', media_url: null, media_type: null, status: 'deleted' }
                            : m
                    )
                };
            });

            // Optimistic update - Conversation list (Sidebar)
            setConversations(prev => {
                const currentConversations = [...prev];
                const index = currentConversations.findIndex(c => c && c.contact && c.contact.phone === phone);
                if (index !== -1) {
                    const conv = currentConversations[index];
                    // Check if the deleted message was the last one shown
                    // We can't easily know if it was the EXACT last one without comparing text/timestamps, 
                    // but it's safe to set "Mensaje eliminado" if it matches roughly or just force update it if we assume the user deletes recent messages.
                    // Better: If we replaced it in the message list, we should check if THIS message was the last one.
                    // Simplified: Just update the last message text to "🚫 Mensaje eliminado" if it matches the deleted message content? 
                    // No, that's hard. Let's just set it to "🚫 Mensaje eliminado" if the timestamp is very recent?
                    // Actually, let's just force update it for visibility.

                    const updatedConv = {
                        ...conv,
                        lastMessage: '🚫 Mensaje eliminado'
                    };
                    currentConversations.splice(index, 1);
                    return [updatedConv, ...currentConversations];
                }
                return prev;
            });

            const res = await apiFetch(`/api/messages/${messageId}`, {
                method: 'DELETE'
            });

            if (!res.ok) {
                throw new Error('Failed to delete');
            }
        } catch (error) {
            console.error(error);
            // Revert logic would go here
        }
    }, []);

    const reactToMessage = useCallback(async (messageId, emoji, phone) => {
        // Optimistic update
        setMessagesByConversation(prev => {
            const current = (prev[phone] || []);
            const updated = current.map(m => {
                if (m.id === messageId) {
                    // Filter out my existing reaction to toggle/replace
                    const otherReactions = (m.reactions || []).filter(r => r.by !== 'me');
                    const newReactions = [...otherReactions];
                    if (emoji) {
                        newReactions.push({ emoji, by: 'me' });
                    }
                    return { ...m, reactions: newReactions };
                }
                return m;
            });
            return {
                ...prev,
                [phone]: updated
            };
        });

        try {
            await apiFetch(`/api/messages/${messageId}/reaction`, {
                method: 'POST',
                body: JSON.stringify({ reaction: emoji, phone })
            });
        } catch (error) {
            console.error('Failed to react:', error);
            // Could revert here if needed
        }
    }, []);

    const removeConversation = useCallback((phone) => {
        setConversations(prev => prev.filter(c => c && c.contact && c.contact.phone !== phone));
        if (selectedConversation && selectedConversation.contact && selectedConversation.contact.phone === phone) {
            setSelectedConversation(null);
        }
    }, [selectedConversation]);

    return {
        conversations,
        selectedConversation,
        messagesByConversation,
        isLoading,
        isLoadingMessages,
        isLoadingMore,
        hasMore,
        currentPage,
        searchQuery,
        aiStatesByPhone,
        globalDefaultAi,
        messagePaginationByPhone,
        isLoadingOlderMessages,
        fetchConversations,
        loadMoreConversations,
        searchConversations,
        fetchMessages,
        selectConversation,
        setSelectedConversation,
        sendMessage,
        sendFile,
        deleteMessage,
        reactToMessage,
        toggleAI,
        togglePin,
        removeConversation,
        markConversationAsRead,
        markConversationAsUnread,
        updateConversationLocal,
        loadOlderMessages
    };
};

export default useConversations;
